import type { ProviderLicense } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';

import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton, EmptyState, ErrorState } from '@/components/ui/state-views';
import { StatusBadge } from '@/components/ui/status-badge';
import { getProviderLicenses } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

function isExpired(dateStr: string) {
  return new Date(dateStr) < new Date();
}

const LICENSE_STATUS_COLORS: Record<string, string> = {
  Active: 'bg-green-50 text-green-700',
  Expired: 'bg-red-50 text-red-700',
};

const columns: Column<ProviderLicense>[] = [
  { key: 'state', header: 'State', render: (l) => <span className='text-neutral-900'>{l.state ?? '\u2014'}</span> },
  { key: 'type', header: 'Type', render: (l) => <span className='text-neutral-700'>{l.type ?? '\u2014'}</span> },
  {
    key: 'license',
    header: 'License #',
    render: (l) => <span className='text-neutral-700'>{l.licenseNumber ?? '\u2014'}</span>,
  },
  {
    key: 'issued',
    header: 'Issued',
    render: (l) => <span className='text-neutral-700'>{formatDate(l.issueDate)}</span>,
  },
  {
    key: 'expires',
    header: 'Expires',
    render: (l) => <span className='text-neutral-700'>{formatDate(l.expirationDate)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (l) => {
      const label = isExpired(l.expirationDate) ? 'Expired' : 'Active';
      return <StatusBadge status={label} colors={LICENSE_STATUS_COLORS} />;
    },
  },
];

export function LicensureTab({ providerId }: { providerId: number }) {
  const {
    data: licenses,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['provider-licenses', providerId],
    queryFn: () => getProviderLicenses(providerId),
  });

  if (error)
    return <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Licenses are unavailable' />;
  if (isLoading) return <DataTableSkeleton columns={columns.length} label='Loading licenses' />;
  if (!licenses || licenses.length === 0) return <EmptyState message='No licenses found.' />;

  return <DataTable columns={columns} data={licenses} keyFn={(l) => l.id} />;
}
